import React from "react";
import type { ReactElement } from "react";

import { Image } from "native-base";

import type { EnrolmentItem } from "pianofunclub-crm/components/ListItems/EnrolmentSelectRow";
import { LOGO_CROPPED_IMG } from "pianofunclub-crm/utils/assets";

import {
  AccountsIcon,
  InvoiceIcon,
  SchoolIcon,
  RegisterIcon,
  TimetableIcon,
  WaitingListIcon,
  PayrollIcon,
  ProfileIcon,
} from "pianofunclub-shared/components/Other/Icons";
import { getFullName } from "pianofunclub-shared/utils/extractors";

export const getTabIcon = (routeName: string, color: string): ReactElement => {
  switch (routeName) {
    case "AccountsHubTab":
      return (
        <>
          <Image resizeMode="contain" size="sm" src={LOGO_CROPPED_IMG} />
          <AccountsIcon color={color} size="md" />
        </>
      );
    case "InvoicingHubTab":
      return <InvoiceIcon color={color} size="md" />;
    case "SchoolsHubTab":
      return <SchoolIcon color={color} size="md" />;
    case "RegistersHubTab":
      return <RegisterIcon color={color} size="lg" />;
    case "TeacherRegistersHubTab":
      return (
        <>
          <Image resizeMode="contain" size="sm" src={LOGO_CROPPED_IMG} />
          <RegisterIcon color={color} size="lg" />;
        </>
      );
    case "TeacherTimetableHubTab":
      return <TimetableIcon color={color} size="lg" />;
    case "WaitingListHubTab":
      return <WaitingListIcon color={color} size="lg" />;
    case "PayrollHubTab":
      return <PayrollIcon color={color} size="lg" />;
    default:
      return <ProfileIcon color={color} size="xl" />;
  }
};

export const getEnrolmentWarningText = (variables: {
  hasInvalidParentPhoneNumber: boolean;
  potentialDuplicatePupils: NonNullable<
    NonNullable<EnrolmentItem>["node"]
  >["potentialDuplicatePupils"];
  potentialDuplicates: NonNullable<NonNullable<EnrolmentItem>["node"]>["potentialDuplicates"];
}) => {
  const { hasInvalidParentPhoneNumber, potentialDuplicatePupils, potentialDuplicates } = variables;

  const potentialDuplicateEnrolmentPupilNames =
    potentialDuplicates?.edges
      ?.map((edge) =>
        edge?.node?.pupilFirstName
          ? getFullName(edge?.node?.pupilFirstName, edge?.node?.pupilLastName)
          : null
      )
      ?.filter((x) => x !== null) ?? [];
  const potentialDuplicatePupilAccountNumbers =
    potentialDuplicatePupils?.edges
      ?.map((edge) => edge?.node?.accountNumber ?? null)
      ?.filter((x) => x !== null) ?? [];

  const hasWarning =
    hasInvalidParentPhoneNumber ||
    potentialDuplicateEnrolmentPupilNames.length > 0 ||
    potentialDuplicatePupilAccountNumbers.length > 0;

  if (!hasWarning) {
    return null;
  }

  const warnings: string[] = [];
  if (potentialDuplicateEnrolmentPupilNames.length > 0) {
    warnings.push(
      `Potential duplicate enrolment${potentialDuplicateEnrolmentPupilNames.length > 1 ? "s" : ""}: ${potentialDuplicateEnrolmentPupilNames.join(", ")}`
    );
  }
  if (potentialDuplicatePupilAccountNumbers.length > 0) {
    warnings.push(
      `Potential duplicate account${potentialDuplicatePupilAccountNumbers.length > 1 ? "s" : ""}: ${potentialDuplicatePupilAccountNumbers.join(", ")}`
    );
  }
  if (hasInvalidParentPhoneNumber) {
    warnings.push("Invalid parent phone number");
  }

  return "⚠️ WARNING:\n" + warnings.join("\n");
};
